// mailListMarkers.ts — Cluster POSTFACH: Merkmale einer Listenzeile (P8)
//
// Drei Fragen soll die Liste beantworten, ohne dass man jede Mail öffnet:
// „hängt ein Dokument dran?", „bin ich darin erwähnt worden?" und — seit
// P7 (PO 14.09.2026) — „gehört diese Mail zu einem Vorgang?".
//
// Die Anhangs-Frage beantwortet `Email.hasAttachments` — in `groupConversations`
// über den GANZEN Thread aggregiert, eine Konversation zeigt die Klammer also
// auch dann, wenn das Dokument an einer früheren Nachricht hängt.
//
// Die Erwähnungs-Frage beantwortet die Mention-Inbox (`GET
// /mail/notes/mentions/me`): jeder Eintrag trägt `mailbox` + `messageId`, ist
// also eindeutig einer Zeile zuzuordnen. Der Schlüssel muss BEIDES enthalten —
// die Suche liefert Treffer aus fremden Postfächern, und eine Graph-Message-Id
// ist nur innerhalb ihres Postfachs eindeutig.

import type { Email } from "../../services/graphMailService";
import type { MailMentionItemDto } from "../../services/mailNoteService";
import type { VorgangsLookupAntwortDto } from "../../services/mailLinkService";

/** Büroklammer an der Zeile — inkl. Anhängen früherer Thread-Nachrichten. */
export function hatAnhang(email: Pick<Email, "hasAttachments">): boolean {
    return email.hasAttachments === true;
}

export type ErwaehnungsIndex = {
    /** Alle Mails, in denen der Nutzer erwähnt wurde (Postfach+MessageId). */
    alle: ReadonlySet<string>;
    /** Teilmenge: Erwähnungen, die der Nutzer noch nicht gelesen hat. */
    ungelesen: ReadonlySet<string>;
};

export const LEERER_ERWAEHNUNGS_INDEX: ErwaehnungsIndex = {
    alle: new Set<string>(),
    ungelesen: new Set<string>(),
};

/** Postfachadressen kommen mal so, mal so geschrieben — Schlüssel normalisieren. */
export function erwaehnungsSchluessel(mailbox: string, messageId: string): string {
    return `${(mailbox || "").trim().toLowerCase()}|${messageId}`;
}

export function baueErwaehnungsIndex(
    list: readonly MailMentionItemDto[],
): ErwaehnungsIndex {
    const alle = new Set<string>();
    const ungelesen = new Set<string>();
    for (const m of list) {
        if (!m.messageId) continue;
        const key = erwaehnungsSchluessel(m.mailbox, m.messageId);
        alle.add(key);
        if (!m.read) ungelesen.add(key);
    }
    return { alle, ungelesen };
}

/**
 * Drei Stufen statt eines Booleans: eine noch ungelesene Erwähnung ist der
 * eigentliche Grund für den Marker und wird deutlich gezeigt; eine bereits
 * gelesene bleibt als leiser Hinweis stehen, damit die Zeile nicht plötzlich
 * ihre Geschichte verliert.
 */
export type ErwaehnungsMarker = "keine" | "gelesen" | "ungelesen";

export function erwaehnungsMarker(
    index: ErwaehnungsIndex,
    mailbox: string | null | undefined,
    messageId: string,
): ErwaehnungsMarker {
    if (!mailbox) return "keine";
    const key = erwaehnungsSchluessel(mailbox, messageId);
    if (index.ungelesen.has(key)) return "ungelesen";
    return index.alle.has(key) ? "gelesen" : "keine";
}

// ── P7 (PO 14.09.2026): „gehört diese Mail zu einem Vorgang?" ───────────────
//
// Die Vorgangs-Frage beantwortet `POST /mail/links/lookup` — EIN Aufruf je
// sichtbarer Listenseite, Antwort je `conversationId`. Konversation statt
// Nachricht, weil der Listen-Repräsentant eines Threads oft nicht die
// Nachricht ist, die seinerzeit verknüpft wurde; der Filter „Vorgänge", State
// A im Lesefenster und der Auto-Link arbeiten genauso.
//
// Der Schlüssel enthält wie bei den Erwähnungen das POSTFACH: Suchtreffer
// stammen aus fremden Postfächern, und dieselbe Konversation kann in zwei
// Postfächern liegen (cbb@ und das eigene) — verknüpft ist sie je Postfach.

export type VorgangsRef = { id: number; titel: string };

/** Postfach+Konversation → die (sichtbaren) Vorgänge, an denen sie hängt. */
export type VorgangsIndex = ReadonlyMap<string, readonly VorgangsRef[]>;

export const LEERER_VORGANGS_INDEX: VorgangsIndex = new Map();

export function vorgangsSchluessel(mailbox: string, conversationId: string): string {
    return `${(mailbox || "").trim().toLowerCase()}|${conversationId}`;
}

/**
 * Antwort des Lookups in den Index einarbeiten. Liefert einen NEUEN Index
 * (React vergleicht Referenzen). `angefragt` ist entscheidend: eine Id, die in
 * der Antwort fehlt, hängt an keinem sichtbaren Vorgang (mehr) — ihr Eintrag
 * wird gelöscht. Sonst bliebe nach dem Lösen einer Verknüpfung der alte Marker
 * stehen.
 */
export function baueVorgangsIndex(
    mailbox: string,
    angefragt: readonly string[],
    antwort: VorgangsLookupAntwortDto,
    basis: VorgangsIndex = LEERER_VORGANGS_INDEX,
): VorgangsIndex {
    const next = new Map(basis);
    for (const convId of angefragt) {
        const key = vorgangsSchluessel(mailbox, convId);
        const vorgaenge = antwort[convId]?.vorgaenge ?? [];
        if (vorgaenge.length > 0) next.set(key, vorgaenge);
        else next.delete(key);
    }
    return next;
}

/**
 * Welche Vorgänge zeigt die Zeile? `null` = keinen Marker.
 *
 * Snapshot-Zeilen (Filter „Vorgänge", `snapshotOnly`) tragen ihren Vorgang
 * schon aus dem Snapshot mit — sie werden nicht nachgefragt. Alle anderen
 * laufen über den Index; ohne Postfach (unbekannte Herkunft) wird nichts
 * behauptet.
 */
export function vorgangsMarker(
    index: VorgangsIndex,
    mailbox: string | null | undefined,
    email: {
        conversationId?: string | null;
        snapshotOnly?: boolean;
        vorgang?: VorgangsRef | null;
    },
): readonly VorgangsRef[] | null {
    if (email.snapshotOnly) return email.vorgang ? [email.vorgang] : null;
    if (!mailbox || !email.conversationId) return null;
    const treffer = index.get(vorgangsSchluessel(mailbox, email.conversationId));
    return treffer && treffer.length > 0 ? treffer : null;
}

/**
 * Tooltip/aria-label des Markers. Bei einem Vorgang steht sein Titel da; bei
 * mehreren die Zahl UND die Titel — sonst wüsste man nicht, welche.
 */
export function vorgangsMarkerLabel(vorgaenge: readonly VorgangsRef[]): string {
    if (vorgaenge.length === 1) return `Gehört zum Vorgang: ${vorgaenge[0].titel}`;
    return `Gehört zu ${vorgaenge.length} Vorgängen: ${vorgaenge.map((v) => v.titel).join(", ")}`;
}

/**
 * Die Vorgänge einer Konversation aus ihren Links (`GET /mail/links?
 * conversationId=`), dedupliziert nach Id, Reihenfolge der Links erhalten
 * (Backend: neueste zuerst). Fürs Lesefenster: dort liegen die Links schon
 * vor, ein zweiter Lookup wäre ein zweiter Request für dieselbe Antwort.
 */
export function vorgaengeAusLinks(
    links: readonly { vorgang: VorgangsRef | null | undefined }[],
): VorgangsRef[] {
    const out: VorgangsRef[] = [];
    const gesehen = new Set<number>();
    for (const l of links) {
        const v = l.vorgang;
        if (!v || gesehen.has(v.id)) continue;
        gesehen.add(v.id);
        out.push({ id: v.id, titel: v.titel });
    }
    return out;
}
